import React, { useEffect } from 'react';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import { useSearchParams } from 'react-router-dom';
import {
  MultiGameScreenConfiguration,
  Payload,
  ShopperExperienceDto,
  SingleGameScreenConfiguration,
} from '@luckycart/xpn-types';

// eslint-disable-next-line
import '@splidejs/react-splide/css';

import {
  WelcomeGameCtaButton,
  WelcomeGameHtmlZone,
  WelcomeGameDisplaySection,
  WelcomeGameImgSection,
  WelcomeGameTxt,
  WelcomeGameThumbnail,
  LeftSliderArrow,
  RightSliderArrow,
} from './WelcomeGameScreen.style';
import { Background } from '../common-components/Background';
import { BlockInfoType } from '../../common-types/BlockInfo.type';
import { ReactComponent as SliderArrowSVG } from '../images/slider-arrow.svg';
import { ALT_ATTRIBUTE_STATIC_MESSAGE } from '../../utils/AltAttributeStaticMessage';
import { Logger } from '../../utils/Logger';
import { eventServiceInstance } from '../../utils/services/EventService';
interface Props {
  config: MultiGameScreenConfiguration | SingleGameScreenConfiguration;
  ctaOnClick?: React.MouseEventHandler<HTMLDivElement>;
  addEditableBlock?: (block: BlockInfoType) => void;
  isEdition?: boolean;
  onBlockEdit?: React.MouseEventHandler<HTMLDivElement>;
  thumbnailUrls: { campId: string; url: string }[];
  welcomeGameScreenType: 'singleGame' | 'multiGame';
  currentShopperExperience?: ShopperExperienceDto<Payload> | null;
}

export const WelcomeGameScreen: React.FunctionComponent<Props> = ({
  addEditableBlock,
  config,
  ctaOnClick,
  isEdition,
  onBlockEdit,
  thumbnailUrls,
  welcomeGameScreenType,
  currentShopperExperience,
}: Props) => {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const siteKey = searchParams.get('siteKey');
    const shopperId = searchParams.get('shopperId');
    const cartId = searchParams.get('cartId');

    if (!currentShopperExperience || siteKey === null || shopperId === null || cartId === null) {
      Logger.log('Missing params to send poppin viewed event', {
        currentShopperExperience,
        siteKey,
        shopperId,
        cartId,
      });
      return;
    }
    
    eventServiceInstance.sendPopinViewedEventToShopperEvent({
      siteKey,
      shopperId,
      cartId,
      operationId: currentShopperExperience.operationId,
      experienceId: currentShopperExperience.experienceId,
    });

  }, [searchParams, currentShopperExperience]);

  return (
    <Background
      displayName="Background"
      sizeInfo={{ height: 1200, width: 1200 }}
      addEditableBlock={addEditableBlock}
      dataTemplateConfig={`screens.${welcomeGameScreenType}.background`}
      backgroundImage={config.background.imageUrl}
      backgroundColor={config.background.color}
      isEdition={isEdition}
      onClick={onBlockEdit}
    >
      <WelcomeGameHtmlZone
        displayName="HTML Zone"
        addEditableBlock={addEditableBlock}
        html={config.mainTextBox.html}
        dataTemplateConfig={`screens.${welcomeGameScreenType}.mainTextBox`}
        isEdition={isEdition}
        onClick={onBlockEdit}
      />
      <WelcomeGameDisplaySection>
        <WelcomeGameTxt
          displayName="HTML Zone"
          addEditableBlock={addEditableBlock}
          html={config.thumbnailTextBox.html}
          dataTemplateConfig={`screens.${welcomeGameScreenType}.thumbnailTextBox`}
          isEdition={isEdition}
          onClick={onBlockEdit}
        />
        <WelcomeGameImgSection>
          <Splide
            hasTrack={false}
            options={{
              type: 'slide',
              pagination: false,
              gap: '4vw',
              perPage: 3,
              perMove: 1,
            }}
          >
            <SplideTrack>
              {thumbnailUrls.map((thumbnail) => (
                <SplideSlide key={thumbnail.campId}>
                  <WelcomeGameThumbnail
                    src={thumbnail.url}
                    alt={ALT_ATTRIBUTE_STATIC_MESSAGE}
                  />
                </SplideSlide>
              ))}
            </SplideTrack>
            <div className="splide__arrows">
              <LeftSliderArrow className="splide__arrow splide__arrow--prev">
                <SliderArrowSVG />
              </LeftSliderArrow>
              <RightSliderArrow className="splide__arrow splide__arrow--next">
                <SliderArrowSVG />
              </RightSliderArrow>
            </div>
          </Splide>
        </WelcomeGameImgSection>
      </WelcomeGameDisplaySection>
      <WelcomeGameCtaButton
        displayName="CTA"
        addEditableBlock={addEditableBlock}
        backgroundColor={config.ctaButton.background.color}
        innerHTML={config.ctaButton.html}
        onClick={onBlockEdit === undefined ? ctaOnClick : onBlockEdit}
        dataTemplateConfig={`screens.${welcomeGameScreenType}.ctaButton`}
        isEdition={isEdition}
      />
    </Background>
  );
};
